import { getExceptionMessage } from "../exceptions/helpers/getExceptionMessage";

/**
 * Extracts a human-readable string from arbitrary error shapes thrown
 * across the app, so we can hand it to UI surfaces (AntD `messageApi`,
 * sonner toasts, inline alerts) without crashing React on objects.
 *
 * Handles:
 *  - Raw strings already used by `rejectWithValue`.
 *  - Backend error payloads under `response.data.error_message`,
 *    `response.data.message`, or `response.data.detail`.
 *  - Native `Error` / Redux Toolkit `SerializedError` objects with a `.message`.
 *  - The HTTP interceptor's reshaped 401/403 payload
 *    `{ response: { data: { code, message } } }` (see `src/api/index.ts`).
 *  - Anything else falls back to the provided default.
 *
 * @param error    The unknown thrown value (catch block argument).
 * @param fallback String to return when nothing usable is found.
 */
export function extractErrorMessage(
  error: unknown,
  fallback = "Ocurrió un error",
): string {
  if (typeof error === "string" && error.trim().length > 0) return error;
  if (error && typeof error === "object") {
    const e = error as Record<string, unknown>;
    const resp = e.response as Record<string, unknown> | undefined;
    const data = resp?.data as Record<string, unknown> | undefined;
    if (data) {
      const errorCode = data.error_code;
      if (typeof errorCode === "string") {
        const mappedMessage = getExceptionMessage(errorCode, "");
        if (mappedMessage) return mappedMessage;
      }
      const backendMessage = data.error_message ?? data.message ?? data.detail;
      if (
        typeof backendMessage === "string" &&
        backendMessage.trim().length > 0
      ) {
        return backendMessage;
      }
    }
    if (typeof e.message === "string" && e.message.trim().length > 0) {
      return e.message;
    }
  }
  return fallback;
}
